
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // EMPTY UNTIL WE HAVE REAL, ATTRIBUTABLE TESTIMONIALS.
  //
  // This array previously held three fabricated testimonials — invented people
  // ("Sarah Johnson", "Michael Chen", "Emily Rodriguez") at invented companies
  // ("TechCorp Global", "FinanceSecure Inc", "DataProtect Solutions"), each with
  // a five-star rating and a stock photo. They were template placeholder content
  // that shipped to production on the home page.
  //
  // A fabricated testimonial is a false or misleading representation about a
  // supplier's services under Australian Consumer Law s.29, and the ACCC treats
  // invented reviews as a distinct enforcement priority. This is not a styling
  // preference — do not repopulate this array with anything you cannot attribute
  // to a real, consenting customer.
  //
  // To restore the section: add entries with a real name, role, organisation and
  // words the person actually said and agreed to publish. The component renders
  // nothing while the array is empty, so the home page simply omits the section.
  const testimonials = [];

  if (testimonials.length === 0) return null;

  const next = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prev = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-[#422680] via-[#660F56] to-[#F54952] bg-clip-text text-transparent">
              What Our Clients Say
            </span>
          </h2>
          {/* Was "Trusted by security professionals worldwide" — an
              unsubstantiated reach claim that would return the moment this
              section is re-enabled. Keep any replacement attributable. */}
          <p className="text-xl text-gray-600">
            In their own words
          </p>
        </motion.div>

        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.3 }}
              className="bg-gradient-to-br from-gray-50 to-white p-12 rounded-3xl border-2 border-gray-100"
            >
              <div className="flex flex-col md:flex-row items-center gap-8">
                <img 
                  src={testimonials[currentIndex].image} 
                  alt={`${testimonials[currentIndex].name}, ${testimonials[currentIndex].role}`} 
                  className="w-32 h-32 rounded-full object-cover border-4 border-[#422680]"
                />
                <div className="flex-1 text-center md:text-left">
                  <div className="flex justify-center md:justify-start gap-1 mb-4">
                    {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                      <Star key={i} className="fill-[#F54952] text-[#F54952]" size={20} />
                    ))}
                  </div>
                  <p className="text-xl text-gray-700 mb-6 italic">"{testimonials[currentIndex].text}"</p>
                  <div>
                    <div className="font-bold text-gray-900 text-lg">{testimonials[currentIndex].name}</div>
                    <div className="text-gray-600">{testimonials[currentIndex].role}</div>
                    <div className="text-[#422680] font-semibold">{testimonials[currentIndex].company}</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="flex justify-center gap-4 mt-8">
            <Button
              onClick={prev}
              variant="outline"
              size="icon"
              className="rounded-full border-2 border-[#422680] text-[#422680] hover:bg-[#422680] hover:text-white"
              aria-label="Previous testimonial"
            >
              <ChevronLeft size={24} />
            </Button>
            <Button
              onClick={next}
              variant="outline"
              size="icon"
              className="rounded-full border-2 border-[#422680] text-[#422680] hover:bg-[#422680] hover:text-white"
              aria-label="Next testimonial"
            >
              <ChevronRight size={24} />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
